<p-card>
  <div class="rep-head">
    <div>
      <h1 class="rep-title">Reportería</h1>
      <p class="rep-sub">
        Cada reporte se genera desde la pantalla donde viven sus datos. Aquí está el catálogo de los que existen y el rastro de quién se
        llevó cuál — con su archivo, el mismo que se entregó.
      </p>
    </div>
    <app-ayuda-cifra titulo="Por qué aquí no hay un botón de «Generar»">
      <p>
        Un reporte sin su pantalla es un reporte sin sus filtros. La cartera sale del mes que estás mirando, la mora de la fecha de corte
        que elegiste y la hoja de carga del archivo que acabas de subir.
      </p>
      <p>
        Repetir esos controles aquí obligaría a mantener la misma decisión en dos lugares, y la copia se desfasaría en el primer parámetro
        nuevo. Cada tarjeta de abajo lleva al lugar donde se saca.
      </p>
      <p class="ay-ojo">
        Lo que sí vive aquí es el <b>archivo tal como se entregó</b>. Volver a generarlo daría otro: la cartera de un mes abierto se mueve
        con cada pago y la mora se recalcula cada noche.
      </p>
    </app-ayuda-cifra>
  </div>

  <!-- ── Catálogo ─────────────────────────────────────────────────────────────────── -->
  <h2 class="rep-h2">Los reportes que existen</h2>

  <div class="rep-grid" *ngIf="!cargandoCatalogo">
    <div class="rep-card" *ngFor="let t of tarjetas">
      <div class="rep-card-top">
        <i [class]="t.icon"></i>
        <span class="rep-card-name">{{ t.label }}</span>
      </div>
      <p class="rep-card-ans">{{ t.answers }}</p>

      <!--
        El pulso. Sin él la tarjeta es un folleto; con él contesta de un vistazo la pregunta con la
        que se entra a esta pantalla: ¿esto lo usa alguien, y quién?
      -->
      <p class="rep-card-pulse" *ngIf="t.total">
        <b>{{ t.total }}</b> generado{{ t.total === 1 ? '' : 's' }}
        <ng-container *ngIf="t.last"> · último el {{ t.last | date: 'dd/MM/yy' }}</ng-container>
        <ng-container *ngIf="t.lastBy"> por {{ t.lastBy }}</ng-container>
      </p>
      <p class="rep-card-pulse is-none" *ngIf="!t.total">Nadie lo ha generado todavía</p>

      <a class="rep-card-go" *ngIf="t.alcanzable" [routerLink]="t.route"> {{ t.where }} <i class="fa-solid fa-arrow-right"></i> </a>
      <!-- Sin permiso el enlace no se apaga en silencio: se dice por qué. -->
      <span class="rep-card-go is-off" *ngIf="!t.alcanzable" pTooltip="Tu rol no tiene acceso a esa pantalla" tooltipPosition="top">
        {{ t.where }} <i class="fa-solid fa-lock"></i>
      </span>
    </div>
  </div>

  <div class="rep-grid" *ngIf="cargandoCatalogo">
    <p-skeleton height="9rem" *ngFor="let s of [1, 2, 3, 4, 5, 6, 7]"></p-skeleton>
  </div>

  <!-- ── Historial ────────────────────────────────────────────────────────────────── -->
  <div class="rep-bar">
    <h2 class="rep-h2 mb-0">Quién se llevó qué</h2>
    <!--
      El `placeholder` no sobra aunque la primera opción diga lo mismo: con `tipo` en `null` el
      desplegable no resuelve ninguna etiqueta y se queda **en blanco**, que se lee como un filtro
      roto en vez de como «sin filtrar».
    -->
    <p-dropdown
      [options]="opcionesTipo"
      [(ngModel)]="tipo"
      (onChange)="filtrarPorTipo()"
      optionLabel="label"
      optionValue="value"
      placeholder="Todos los reportes"
      styleClass="rep-filter"
      appendTo="body"
    ></p-dropdown>
  </div>

  <p-table
    [value]="filas"
    [lazy]="true"
    [loading]="cargandoHistorial"
    [paginator]="true"
    [rows]="limit"
    [totalRecords]="total"
    [first]="page * limit"
    (onLazyLoad)="cargarHistorial($event)"
    styleClass="rep-table p-datatable-sm"
  >
    <ng-template pTemplate="header">
      <tr>
        <th style="width: 10rem">Cuándo</th>
        <th>Reporte</th>
        <th>Con qué</th>
        <th>Quién lo pidió</th>
        <th class="rep-num">Filas</th>
        <th class="rep-num">Peso</th>
        <th style="width: 6rem"></th>
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-f>
      <tr>
        <td>{{ f.generationDate | date: 'dd/MM/yy HH:mm' }}</td>
        <td class="rep-name"><i [class]="icono(f.reportType)"></i> {{ etiqueta(f.reportType) }}</td>
        <td class="rep-params">{{ conQue(f) }}</td>
        <td>{{ quien(f) }}</td>
        <td class="rep-num">{{ f.recordCount || '—' }}</td>
        <td class="rep-num" [pTooltip]="'Se generó en ' + duracion(f.durationMs)" tooltipPosition="left">{{ peso(f.fileSizeBytes) }}</td>
        <td>
          <button
            *ngIf="f.fileUrl"
            pButton
            type="button"
            icon="fa-solid fa-download"
            class="p-button-sm p-button-text"
            pTooltip="Bajar este archivo, el mismo que se entregó"
            tooltipPosition="left"
            [loading]="bajando === f._id"
            (click)="descargar(f)"
          ></button>
          <!--
            Sin archivo no hay botón. Los reportes generados antes de que se guardaran no lo tienen,
            y un botón que siempre falla es peor que su ausencia explicada.
          -->
          <span
            *ngIf="!f.fileUrl"
            class="rep-nofile"
            pTooltip="Este reporte se generó antes de que se guardaran los archivos. Vuelve a generarlo desde su pantalla."
            tooltipPosition="left"
            >sin archivo</span
          >
        </td>
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="7">
          <div class="rep-empty">
            <i class="fa-solid fa-inbox"></i>
            <p>Todavía no se ha generado ningún reporte{{ tipo ? ' de este tipo' : '' }}.</p>
            <p class="rep-empty-why">En cuanto alguien baje uno desde su pantalla, aparecerá aquí con su archivo.</p>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</p-card>

<p-toast position="top-right"></p-toast>
